import { useCallback, useEffect, useRef, useState } from 'react';
import { analyzeBrainer } from 'api/analysisApi';
import type { Engine } from 'api/analysisApi';
import { ApiError } from 'api/restClient';
import type { AnalysisResult } from 'types/analysis';

type Exchange = 'binance' | 'mexc';
type Status = 'idle' | 'loading' | 'success' | 'error' | 'aborted';

export interface UseAnalyzeBrainerResult {
  status: Status;
  data: AnalysisResult | null;
  error: string | null;
  /** When the current result was produced (ms epoch), for cooldown display. */
  updatedAt: number | null;
  /** Kick off (or re-run) the analysis for the given asset/tf/exchange. */
  run: () => void;
  /** Cancel the in-flight analysis (no-op if nothing is running). */
  abort: () => void;
  /** Clear any in-flight request and reset to idle (e.g. on param change). */
  reset: () => void;
}

/**
 * On-demand deep-analysis runner. Unlike the live WS subscription this is a
 * one-shot REST call the user triggers via the Analyze action. Aborts any
 * in-flight request when params change or the component unmounts.
 */
export function useAnalyzeBrainer(
  asset: string,
  timeframe: string,
  exchange: Exchange,
  engine: Engine = 'enforcer',
): UseAnalyzeBrainerResult {
  const [status, setStatus] = useState<Status>('idle');
  const [data, setData] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus('idle');
    setData(null);
    setError(null);
    setUpdatedAt(null);
  }, []);

  // Abort + reset whenever the target or engine changes so stale results never show.
  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, timeframe, exchange, engine]);

  const run = useCallback(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus('loading');
    setError(null);

    analyzeBrainer(asset, timeframe, exchange, engine, true, ctrl.signal)
      .then((res) => {
        if (!mountedRef.current || ctrl.signal.aborted) return;
        setData(res);
        setUpdatedAt(Date.now());
        setStatus('success');
      })
      .catch((e: unknown) => {
        if (!mountedRef.current || ctrl.signal.aborted) return;
        const msg =
          e instanceof ApiError
            ? `${e.message} (${e.status})`
            : e instanceof Error
              ? e.message
              : 'Analysis failed';
        setError(msg);
        setStatus('error');
      });
  }, [asset, timeframe, exchange, engine]);

  // User-initiated cancel of an in-flight run. Distinct from reset(): keeps the
  // last data and lands on an 'aborted' status (the panel shows a "Run again"
  // prompt) rather than dropping back to the idle "Ready to analyze" state.
  const abort = useCallback(() => {
    if (!abortRef.current) return;
    abortRef.current.abort();
    abortRef.current = null;
    setStatus('aborted');
  }, []);

  return { status, data, error, updatedAt, run, abort, reset };
}
